import axios from "axios";
import { IFileUpload, IValidationResult } from "lobbie";
import { FileWithPath } from "react-dropzone";
import { BASE_API_URL, BASE_SECURE_ROUTE, DEFAULT_ERROR_MESSAGE } from "src/constants";
import { handleError, isFailedRequest, notify } from "src/utils";

export interface INamedBlob {
    data: Blob;
    name: string;
}

export interface IUploadOptions {
    onProgress?: (s3ObjectPath: string, fileName: string, progress: number) => void;
    onDone?: (fileUpload: IFileUpload, progress: number) => void;
    onError?: (error: Error, s3ObjectPath?: string) => void;
    extra?: Record<string, string | number | null>;
}

// Create a signed upload URL
export async function createSignedUpload(file: FileWithPath | INamedBlob, accountId: string) {
    const workingFile = (file as FileWithPath).size
        ? (file as FileWithPath)
        : (file as INamedBlob).data;
    if (!accountId || !workingFile?.size) {
        return { s3ObjectPath: "", signedURL: "" };
    }
    try {
        const response = await axios.post(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/shared/file_uploads/new`,
            {
                accountId,
                type: workingFile.type,
                size: workingFile.size,
            },
            { withCredentials: true },
        );
        if (!response.data) {
            handleError(new Error("No presigned URL returned - file upload failed."));
            return { signedURL: "", s3ObjectPath: "" };
        } else if (isFailedRequest(response.data)) {
            notify({
                level: "error",
                title: "Failed to upload file",
                message: (response.data as IValidationResult)?.message || DEFAULT_ERROR_MESSAGE,
            });
            return { signedURL: "", s3ObjectPath: "" };
        }

        return response.data as { s3ObjectPath: string; signedURL: string };
    } catch (e) {
        handleError(e as Error);
        return { signedURL: "", s3ObjectPath: "" };
    }
}

export async function uploadFileToS3(
    file: FileWithPath | INamedBlob,
    signedURL: string,
    s3ObjectPath: string,
    options: IUploadOptions = {},
): Promise<boolean> {
    return new Promise((resolve) => {
        if (options.onProgress) {
            options.onProgress(s3ObjectPath, file.name, 0.01);
        }
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signedURL, true);
        xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable && options.onProgress) {
                options.onProgress(
                    s3ObjectPath,
                    file.name,
                    Math.ceil((e.loaded / e.total) * 100.0),
                );
            }
        });
        xhr.onabort = () => {
            if (options.onError) {
                options.onError(new Error("XHR file upload request aborted."));
            } else {
                handleError(new Error("XHR file upload request aborted."));
            }
            resolve(false);
        };
        xhr.onload = () => {
            if (xhr.status.toString().match(/^2/)) {
                resolve(true);
            } else {
                let errorMessage = `uploadFileToS3 - XHR file upload request failed. Status: ${xhr.status}: ${xhr.statusText}`;
                if (options.onError) {
                    options.onError(new Error(errorMessage), s3ObjectPath);
                } else {
                    handleError(new Error(errorMessage));
                }
                resolve(false);
            }
        };
        xhr.onerror = () => {
            let errorMessage = `uploadFileToS3 - XHR file upload request failed. Status: ${xhr.status}: ${xhr.statusText}`;
            if (options.onError) {
                options.onError(new Error(errorMessage), s3ObjectPath);
            } else {
                handleError(new Error(errorMessage));
            }
            resolve(false);
        };
        const data = (file as FileWithPath).size
            ? (file as FileWithPath)
            : (file as INamedBlob).data;
        xhr.send(data);
    });
}

export async function createFileUploadRecord(
    s3ObjectPath: string,
    file: FileWithPath | INamedBlob,
    options: IUploadOptions = {},
): Promise<IFileUpload | undefined> {
    const workingFile = (file as FileWithPath).size
        ? (file as FileWithPath)
        : (file as INamedBlob).data;
    try {
        const response = await axios.post(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/shared/file_uploads/create`,
            {
                s3ObjectPath,
                fileName: file.name,
                type: workingFile.type,
                ...options.extra,
            },
            { withCredentials: true },
        );
        if (response.data && !isFailedRequest(response.data)) {
            const fileUpload = response.data as IFileUpload;
            options.onDone?.(fileUpload, 100);
            return fileUpload;
        }
    } catch (error) {
        if (options.onError) {
            options.onError(error as Error, s3ObjectPath);
        } else {
            handleError(error as Error);
        }
    }
    return undefined;
}

export async function deleteUploadedFile(s3ObjectPath: string | undefined): Promise<void> {
    if (!s3ObjectPath) return;
    try {
        await axios.post(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/shared/file_uploads/delete`,
            { s3ObjectPath },
            { withCredentials: true },
        );
    } catch (error) {
        handleError(error as Error);
    }
}

export async function uploadFile(
    file: INamedBlob,
    accountId: string,
    options: IUploadOptions = {},
): Promise<IFileUpload | undefined> {
    try {
        // Step 1: Get signed URL
        const { s3ObjectPath, signedURL } = await createSignedUpload(file, accountId);
        if (!signedURL) {
            return undefined;
        }
        // Step 2: Upload to S3
        const uploadSuccess = await uploadFileToS3(file, signedURL, s3ObjectPath, options);
        if (!uploadSuccess) {
            notify({
                level: "error",
                title: "Error uploading file",
                message: DEFAULT_ERROR_MESSAGE,
            });
            return undefined;
        }
        // Step 3: Create file record
        return await createFileUploadRecord(s3ObjectPath, file, options);
    } catch (error) {
        if (options.onError) {
            options.onError(error as Error);
        } else {
            handleError(error as Error);
        }
        return undefined;
    }
}
